import { useMemo } from 'react'
import { FieldShell } from '../FieldShell'
import { useFieldContext, type FieldComponentProps } from '../rendererContext'
import type { MultiConfig } from '../types'
import { OptionsStatus } from './OptionsStatus'
import { optionByKey, optionKey, valuesEqual, visibleOptions, withTextLabels } from './optionUtils'
import { controlClass, useControlMeta } from './useControlMeta'
import { useResolvedOptions } from './useResolvedOptions'

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : []
}

export function MultiSelectField({ field, path, value, errors }: FieldComponentProps) {
  const meta = useControlMeta(field, path, errors)
  const { enhancedSelectAdapter: EnhancedSelect } = useFieldContext()
  const { options: rawOptions, loading, error } = useResolvedOptions(field)
  const options = useMemo(() => withTextLabels(rawOptions), [rawOptions])
  const selected = asArray(value)
  const shown = visibleOptions(options, (v) => selected.some((s) => valuesEqual(s, v)))
  const config = field.config as MultiConfig | undefined

  if (field.display_only) {
    const labels = shown
      .filter((opt) => selected.some((s) => valuesEqual(s, opt.value)))
      .map((opt) => opt.label)
    return (
      <FieldShell field={field} path={path} errors={errors}>
        <div id={meta.id} className="form-control-plaintext">
          {labels.join(', ')}
        </div>
      </FieldShell>
    )
  }

  // Select2-style control when the host wired an adapter; `plain_select`
  // forces the native multiple select per field.
  if (EnhancedSelect != null && config?.plain_select !== true) {
    return (
      <FieldShell field={field} path={path} errors={errors}>
        <EnhancedSelect
          id={meta.id}
          options={shown}
          selected={selected}
          disabled={meta.disabled}
          placeholder={config?.placeholder}
          hasErrors={meta.hasErrors}
          ariaInvalid={meta.ariaInvalid}
          ariaRequired={meta.ariaRequired}
          ariaDescribedBy={meta.describedBy}
          maxItems={config?.max_items}
          classNames={meta.classNames}
          onChange={(picked) => meta.setFieldValue(path, picked)}
          onBlur={() => meta.notifyBlur(path)}
        />
        <OptionsStatus loading={loading} error={error} />
      </FieldShell>
    )
  }

  return (
    <FieldShell field={field} path={path} errors={errors}>
      <select
        id={meta.id}
        multiple
        className={controlClass('form-select', meta.classNames.select, meta.hasErrors)}
        value={selected.map(optionKey)}
        disabled={meta.disabled}
        aria-invalid={meta.ariaInvalid}
        aria-required={meta.ariaRequired}
        aria-describedby={meta.describedBy}
        onChange={(event) => {
          const picked = Array.from(event.target.selectedOptions)
            .map((o) => optionByKey(shown, o.value)?.value)
            .filter((v) => v !== undefined)
          meta.setFieldValue(path, picked)
        }}
        onBlur={() => meta.notifyBlur(path)}
      >
        {shown.map((opt) => (
          <option key={optionKey(opt.value)} value={optionKey(opt.value)}>
            {opt.label}
          </option>
        ))}
      </select>
      <OptionsStatus loading={loading} error={error} />
    </FieldShell>
  )
}
